import { useState, useEffect, ReactNode } from 'react';
import { supabase, isSupabaseConfigured } from '../../lib/supabaseClient';
import { getProfile, signOut as authSignOut } from './authService';
import { AuthContext } from './useAuth';
import type { Session } from '@supabase/supabase-js';
import type { Profile } from '../../lib/supabaseClient';

interface AuthProviderProps {
  children: ReactNode;
}

export default function AuthProvider({ children }: AuthProviderProps) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Si Supabase no está configurado, marcar como no loading
    if (!isSupabaseConfigured() || !supabase) {
      setLoading(false);
      return;
    }

    // Obtener sesión inicial
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session?.user) {
        loadProfile(session.user.id);
      } else {
        setLoading(false);
      }
    });

    // Escuchar cambios de autenticación
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (session?.user) {
        loadProfile(session.user.id);
      } else {
        setProfile(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  async function loadProfile(userId: string) {
    try {
      const profileData = await getProfile(userId);
      setProfile(profileData);
    } catch (error) {
      console.error('Error al cargar perfil:', error);
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }

  async function handleSignOut() {
    try {
      await authSignOut();
      setSession(null);
      setProfile(null);
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
      throw error;
    }
  }

  const value = {
    session,
    profile,
    loading,
    role: profile?.role || null,
    signOut: handleSignOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
